import React, { useState, useEffect } from 'react';
import './LoginPage.css';
import { getErrorMessage } from '../utils/api';
import { API_BASE_URL, buildApiUrl } from '../config/api';

interface LoginPageProps {
  userType: 'coaches' | 'players' | 'parents';
  onBackToLanding: () => void;
  onLoginSuccess?: (isAdmin?: boolean, playerId?: number) => void;
  onRegisterClick?: (userType: 'coaches' | 'players' | 'parents') => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ userType, onBackToLanding, onLoginSuccess, onRegisterClick }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Clear form when component mounts or userType changes
  useEffect(() => {
    setUsername('');
    setPassword('');
    setError(null);
    setShowPassword(false);
    
    // Additional autocomplete prevention - disable autocomplete on all inputs
    const preventAutocomplete = () => {
      const inputs = document.querySelectorAll('input[type="password"], input[type="text"]');
      inputs.forEach((input: any) => {
        if (input) {
          input.setAttribute('autocomplete', 'off');
          input.setAttribute('autocapitalize', 'off');
          input.setAttribute('autocorrect', 'off');
          input.setAttribute('spellcheck', 'false');
          input.setAttribute('data-lpignore', 'true');
          input.setAttribute('data-1p-ignore', 'true');
          input.setAttribute('data-bwignore', 'true');
          input.setAttribute('data-form-type', 'other');
          // Prevent browser from showing autocomplete
          input.addEventListener('focus', (e: any) => {
            e.target.setAttribute('autocomplete', 'off');
          }, { once: true });
        }
      });
    };
    
    setTimeout(preventAutocomplete, 50);
    setTimeout(preventAutocomplete, 200);
    setTimeout(preventAutocomplete, 500);
  }, [userType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // API_BASE_URL is now defined at component level
    // Convert plural userType to singular for backend API
    const userTypeForAPI = userType === 'coaches' ? 'coach' : userType === 'players' ? 'player' : 'parent';
    
    // Declare loginUrl outside try block so it's accessible in catch
    const loginUrl = buildApiUrl('/auth/login');
    console.log('Login attempt:', {
      apiUrl: loginUrl,
      userType: userTypeForAPI,
      username: username,
      fromIframe: window.self !== window.top
    });

    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        mode: 'cors', // Explicitly set CORS mode
        body: JSON.stringify({
          username: username,
          password: password,
          user_type: userTypeForAPI, // Backend expects singular: 'coach', 'player', or 'parent'
        }),
      });

      // Handle network errors - if fetch fails, response won't exist
      // Note: fetch() throws TypeError on network failure, so this check may not be reached

      // Try to parse JSON response, but handle errors gracefully
      let data: any = {};
      try {
        const responseText = await response.text();
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error('Server returned an invalid response. Please check if the backend is running correctly.');
      }
      
      console.log('Login response:', {
        status: response.status,
        ok: response.ok,
        hasToken: !!data.access_token,
        error: data.detail || data.message
      });

      if (response.ok) {
        // Store JWT token and user info in localStorage
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('futeval_access_token', data.access_token); // Also store as futeval token for Parent Portal
          localStorage.setItem('user_id', data.user_id?.toString() || '');
          localStorage.setItem('user_role', data.role || '');
          localStorage.setItem('username', data.username || '');
          localStorage.setItem('is_admin', (data.is_admin || false).toString());
          
          // Send token to parent window if we're in an iframe (for Parent Portal integration)
          if (window.self !== window.top) {
            try {
              window.parent.postMessage({
                type: 'FUTEVAL_TOKEN',
                token: data.access_token
              }, '*'); // Use '*' for cross-origin, or specify parent origin for security
              console.log('Token sent to parent window');
            } catch (e) {
              console.warn('Could not send token to parent:', e);
            }
          }
        }
        
        if (onLoginSuccess) {
          if (userType === 'coaches') {
            onLoginSuccess(data.is_admin || false);
          } else if (userType === 'players') {
            onLoginSuccess(false);
          } else if (userType === 'parents') {
            onLoginSuccess(false);
          }
        }
      } else {
        setError(getErrorMessage(data, 'Login failed. Please check your credentials.'));
      }
    } catch (err: any) {
      console.error('Login error:', err);
      console.error('Error type:', err?.name, 'Error message:', err?.message);
      console.error('API URL used:', loginUrl);
      
      // Provide more specific error messages
      if (err.name === 'TypeError' && (err.message?.includes('fetch') || err.message?.includes('Load failed') || err.message?.includes('Failed to fetch'))) {
        setError(`Network error: Cannot connect to server at ${loginUrl}. Please check your internet connection and try again.`);
      } else if (err.message && err.message.includes('Failed to connect')) {
        setError('Cannot connect to server. Please make sure the backend is running.');
      } else if (err.message && err.message.includes('fetch')) {
        setError('Network error: Cannot reach the server. Please check if the backend is running.');
      } else {
        setError(getErrorMessage(err, 'Login failed. Please check your connection and try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    if (userType === 'coaches') {
      return "Welcome Back Coach! Sign in to access your Player Evaluation Form or to search for your players.";
    }
    if (userType === 'parents') {
      return "Welcome Back! Sign in to access your child's evaluation dashboard";
    }
    return "Welcome Back! Sign in to access your evaluation dashboard";
  };

  const getRegisterText = () => {
    if (userType === 'parents') {
      return 'Register as Parent';
    } else if (userType === 'players') {
      return 'Register as Player';
    }
    return 'Register as Coach';
  };

  // API_BASE_URL is imported from config/api.ts at the top

  return (
    <div className="login-page">
      <div className="login-container">
        <h2>{getGreeting()}</h2>
        <div style={{fontSize:'12px', opacity:0.7, marginBottom:'8px'}}>API: {API_BASE_URL}</div>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          {/* Hidden dummy fields to prevent browser autocomplete */}
          <input type="text" style={{ position: 'absolute', left: '-9999px', opacity: 0, width: 0, height: 0 }} autoComplete="off" tabIndex={-1} readOnly aria-hidden="true" />
          <input type="password" style={{ position: 'absolute', left: '-9999px', opacity: 0, width: 0, height: 0 }} autoComplete="off" tabIndex={-1} readOnly aria-hidden="true" />
          <input type="email" style={{ position: 'absolute', left: '-9999px', opacity: 0, width: 0, height: 0 }} autoComplete="off" tabIndex={-1} readOnly aria-hidden="true" />
          <div className="form-group">
            <label>Username or Email</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="off"
              name="username-hidden"
              id="username-hidden"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
              data-bwignore="true"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                name="password-hidden"
                id="password-hidden"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
                data-bwignore="true"
                style={{ paddingRight: '40px' }}
                onFocus={(e) => {
                  // Prevent autocomplete on focus
                  e.target.setAttribute('autocomplete', 'new-password');
                  e.target.setAttribute('data-lpignore', 'true');
                  e.target.setAttribute('data-1p-ignore', 'true');
                }}
                onKeyDown={(e) => {
                  // Prevent autocomplete on keydown
                  if (e.key !== 'Tab') {
                    e.currentTarget.setAttribute('autocomplete', 'new-password');
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#567ad8'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => {
              // Use console.log instead of alert to avoid popup
              console.log('Forgot password functionality coming soon. Please contact your administrator.');
            }}
            className="link-btn"
            style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}
          >
            Forgot Password or Username?
          </button>
        </div>
        {onRegisterClick && (
          <p className="register-link">
            New User?{' '}
            <button onClick={() => onRegisterClick(userType)} className="link-btn">
              {getRegisterText()}
            </button>
          </p>
        )}
        <button onClick={onBackToLanding} className="back-btn" style={{ color: '#ff0000', borderColor: '#ff0000' }}>
          LOGOUT
        </button>
      </div>
    </div>
  );
};

export default LoginPage;

